// game/client/src/buildings/z-menu.ts
//
// Build menu — the "Z" shelf for placing buildings. Rebuilt to the hud-v10
// target (docs/design/mockups/hud-v10-build-menu.html): a centered gold-rim
// panel with four category tabs (Survival / Crafting / Camp / Defense),
// preview-icon tiles, resource-icon costs, affordability/lock states, and a
// hover tooltip.
//
// Controls (wired in main.ts): Z toggles · Tab switches category · number keys
// 1-9 pick the nth building in the active tab (Q/W/E stay free for abilities).
// Selection publishes via onSelect → main.ts hands the type to the placement
// preview and closes the menu.
import { BUILDING_DEFS, getMaterial, type BuildingType, type Cube } from "@tribez/shared";

export interface ZMenu {
  toggle(): void;
  open(): void;
  close(): void;
  isOpen(): boolean;
  /** Update affordability based on current player counters. Call per frame. */
  refresh(player: Cube | null): void;
  /** Subscribe to selection events (user picks a building). */
  onSelect(handler: (type: BuildingType) => void): void;
  /** Switch to the next/previous category tab (Tab / Shift+Tab). */
  cycleTab(dir?: 1 | -1): void;
  /** Pick the nth (1-based) building in the active tab (number-row keys). */
  selectHotkey(n: number): void;
  /** Human-readable cost string for a building (used by other HUD surfaces). */
  getCost(type: BuildingType): string;
}

interface TabDef { id: string; label: string; types: BuildingType[]; }

// Category grouping + within-tab order — mirrors the hud-v10 mockup. Within-tab
// position drives the 1-9 hotkey and the corner badge.
const TABS: TabDef[] = [
  { id: "survival", label: "Survival", types: ["Tent", "Campfire", "Farm"] },
  { id: "crafting", label: "Crafting", types: ["Forge", "Workbench"] },
  { id: "camp",     label: "Camp",     types: ["Tavern", "HuntersLodge", "Wonder", "StorageOutpost"] },
  { id: "defense",  label: "Defense",  types: ["Palisade", "AutomatedDefenseTurret", "SpikeTrap"] },
];

const DISPLAY_NAME: Partial<Record<BuildingType, string>> = {
  HuntersLodge: "Hunter's Lodge",
  StorageOutpost: "Storage Outpost",
  AutomatedDefenseTurret: "Defense Turret",
  SpikeTrap: "Spike Trap",
};
function nameFor(t: BuildingType): string { return DISPLAY_NAME[t] ?? t; }

// One short line per building (shown in the hover tooltip).
const BLURB: Record<BuildingType, string> = {
  Tent: "Stamina aura + shelter.",
  Campfire: "Warmth + cooking.",
  Farm: "Lure foxes · tame sheep.",
  Forge: "Craft weapons & armor.",
  Workbench: "Craft tools & gear.",
  Tavern: "Buy spells · forward respawn.",
  HuntersLodge: "Pet mastery & upgrades.",
  Wonder: "Win condition — defend it.",
  StorageOutpost: "Trade & stockpile resources.",
  Palisade: "Wall — blocks enemies.",
  AutomatedDefenseTurret: "Auto-fires on enemies.",
  SpikeTrap: "Damages enemies who cross.",
};

// Placement requirement (lock). EMPTY today — building-placement gates were
// removed for KTS fidelity (Artisanship only scales cost + crafting tiers). The
// locked-tile UI is data-driven scaffolding: add a string here to re-introduce a
// gate and the tile renders locked automatically. (A real gate also needs a
// met-check against player state where `isLocked` is evaluated below.)
const REQUIREMENT: Partial<Record<BuildingType, string>> = {};

// --- Icon resolver -----------------------------------------------------------
// SVG placeholders today. To drop in a generated art asset for a building, add
// an entry to BUILDING_ICON_ASSET (path under client/public). iconFor() then
// emits an <img>, with zero markup changes anywhere else.
const BUILDING_ICON_ASSET: Partial<Record<BuildingType, string>> = {};

const BUILDING_ICON_SVG: Record<BuildingType, string> = {
  Tent: `<svg viewBox="0 0 62 62"><path d="M31 8 L54 52 L8 52 Z" fill="#8b6a3d" stroke="#4a2818" stroke-width="2"/><path d="M31 8 L31 52" stroke="#4a2818" stroke-width="2"/><path d="M24 52 L31 34 L38 52 Z" fill="#3a2515"/></svg>`,
  Campfire: `<svg viewBox="0 0 62 62"><path d="M12 50 L50 44 M14 44 L48 50" stroke="#6a4a28" stroke-width="4" stroke-linecap="round"/><path d="M31 12 Q41 26 31 41 Q21 30 31 12 Z" fill="#f7a83c"/><path d="M31 22 Q36 30 31 39 Q27 32 31 22 Z" fill="#ffd66b"/></svg>`,
  Farm: `<svg viewBox="0 0 62 62"><rect x="12" y="28" width="38" height="26" fill="#9a5a3a" stroke="#4a2818" stroke-width="2"/><path d="M10 28 L31 12 L52 28" fill="#6a3a24" stroke="#2a1a0a" stroke-width="2"/><rect x="27" y="38" width="8" height="16" fill="#3a2515"/></svg>`,
  Forge: `<svg viewBox="0 0 62 62"><path d="M10 24 L46 24 L46 31 L34 31 L40 40 L23 40 Q19 33 12 33 Z" fill="#6a7078" stroke="#2a3038" stroke-width="2"/><rect x="25" y="40" width="12" height="13" fill="#3a2515" stroke="#1a0a05"/></svg>`,
  Workbench: `<svg viewBox="0 0 62 62"><rect x="10" y="28" width="42" height="7" fill="#8b6a3d" stroke="#4a2818" stroke-width="2"/><rect x="14" y="35" width="6" height="18" fill="#6a4a28"/><rect x="42" y="35" width="6" height="18" fill="#6a4a28"/><rect x="34" y="16" width="4" height="12" fill="#6a4a28"/><rect x="30" y="14" width="13" height="6" fill="#c0c8d0" stroke="#444"/></svg>`,
  Tavern: `<svg viewBox="0 0 62 62"><rect x="13" y="24" width="36" height="32" fill="#8b6a3d" stroke="#4a2818" stroke-width="2"/><path d="M11 24 L31 6 L51 24" fill="#5a3a20" stroke="#2a1a0a" stroke-width="2"/><rect x="27" y="35" width="8" height="21" fill="#3a2515" stroke="#1a0a05"/><rect x="17" y="30" width="6" height="6" fill="#f7c360"/><rect x="39" y="30" width="6" height="6" fill="#f7c360"/></svg>`,
  HuntersLodge: `<svg viewBox="0 0 62 62"><rect x="11" y="22" width="40" height="34" fill="#7a5630" stroke="#3a2010" stroke-width="2"/><path d="M9 22 L31 8 L53 22" fill="#5a3a20" stroke="#2a1a0a" stroke-width="2"/><rect x="26" y="34" width="10" height="22" fill="#2a1810"/><path d="M19 30 L23 26 M39 30 L43 26" stroke="#c89860" stroke-width="1.5"/></svg>`,
  Wonder: `<svg viewBox="0 0 62 62"><path d="M31 6 L50 30 L46 56 L16 56 L12 30 Z" fill="#caa84e" stroke="#6a5018" stroke-width="2"/><path d="M31 6 L50 30 L31 22 Z" fill="#e2c266"/><rect x="25" y="40" width="12" height="16" fill="#5a4018"/><circle cx="31" cy="20" r="3.5" fill="#fff0c0"/></svg>`,
  StorageOutpost: `<svg viewBox="0 0 62 62"><rect x="9" y="20" width="44" height="36" fill="#a88860" stroke="#4a2818" stroke-width="2"/><line x1="9" y1="32" x2="53" y2="32" stroke="#4a2818" stroke-width="1"/><rect x="25" y="38" width="12" height="18" fill="#3a2515"/><path d="M7 20 L31 8 L55 20" fill="#5a3a20" stroke="#2a1a0a" stroke-width="2"/></svg>`,
  Palisade: `<svg viewBox="0 0 62 62"><g fill="#7a5630" stroke="#3a2010" stroke-width="1.5"><path d="M13 22 L18 16 L18 54 L13 54 Z"/><path d="M22 22 L27 16 L27 54 L22 54 Z"/><path d="M31 22 L36 16 L36 54 L31 54 Z"/><path d="M40 22 L45 16 L45 54 L40 54 Z"/></g><rect x="11" y="34" width="40" height="4" fill="#5a3f1e"/></svg>`,
  AutomatedDefenseTurret: `<svg viewBox="0 0 62 62"><rect x="21" y="24" width="20" height="30" fill="#8a8578" stroke="#3a3530" stroke-width="2"/><rect x="16" y="14" width="30" height="11" fill="#6a6058" stroke="#2a2520" stroke-width="2"/><g fill="#2a2520"><rect x="19" y="16" width="4" height="5"/><rect x="29" y="16" width="4" height="5"/><rect x="39" y="16" width="4" height="5"/></g></svg>`,
  SpikeTrap: `<svg viewBox="0 0 62 62"><ellipse cx="31" cy="48" rx="22" ry="6" fill="#3a2515"/><g fill="#c0c8d0" stroke="#5a6068" stroke-width="1"><path d="M15 48 L19 30 L23 48 Z"/><path d="M27 48 L31 26 L35 48 Z"/><path d="M39 48 L43 30 L47 48 Z"/></g></svg>`,
};

function iconFor(t: BuildingType): string {
  const asset = BUILDING_ICON_ASSET[t];
  if (asset) return `<img class="bm-tile__img" src="${asset}" alt="" draggable="false">`;
  return BUILDING_ICON_SVG[t] ?? "";
}

// Resource cost glyphs (13×13 viewBox) — colour-keyed per resource.
const RES_ICON: Record<string, string> = {
  lumber: '<rect x="1" y="4" width="11" height="7" fill="#8b6a3d"/>',
  stone: '<path d="M2 10 L5 3 L9 3 L11 10 Z" fill="#8a8578"/>',
  ironOre: '<path d="M6.5 1 L12 6.5 L6.5 12 L1 6.5 Z" fill="#c9d4e0"/>',
  woolOrLeather: '<path d="M3 3 L10 3 L12 6 L10 11 L3 11 L1 6 Z" fill="#8b5a2b"/>',
};
const COST_ORDER: Array<keyof typeof RES_ICON> = ["lumber", "stone", "ironOre", "woolOrLeather"];

interface CostLine { key: string; amount: number; }
function costLines(type: BuildingType): CostLine[] {
  const c = BUILDING_DEFS[type].cost as Record<string, number | undefined>;
  const out: CostLine[] = [];
  for (const key of COST_ORDER) {
    const amt = c[key] ?? 0;
    if (amt > 0) out.push({ key, amount: amt });
  }
  return out;
}

/** True if the player can't cover this single cost line. */
function lineShort(player: Cube | null, key: string, amount: number): boolean {
  if (!player) return true;
  const have = key === "woolOrLeather"
    ? getMaterial(player, "wool") + getMaterial(player, "leather")
    : getMaterial(player, key as never);
  return amount > have;
}

function isLocked(type: BuildingType): boolean {
  // Dormant: only locked when a requirement string exists. (When a real gate
  // is introduced, AND it against a met-check on player expertise here.)
  return REQUIREMENT[type] != null;
}

// DOM refs cached per tile so refresh() mutates classes in place (stable click
// targets — re-rendering innerHTML every frame detaches the element a click is
// resolving and the click misses).
interface TileRef { type: BuildingType; el: HTMLDivElement; costEls: HTMLSpanElement[]; lines: CostLine[]; }

export function createZMenu(): ZMenu {
  const root = document.createElement("div");
  root.className = "build-menu";
  root.style.display = "none";
  root.innerHTML = `
    <div class="bm-header">
      <div class="bm-title">✧ Build Menu ✧</div>
      <div class="bm-close"><span class="bm-key">Tab</span> switch · <span class="bm-key">Z</span> close</div>
    </div>
    <div class="bm-tabs"></div>
    <div class="bm-main"></div>`;
  document.body.appendChild(root);

  const tabsEl = root.querySelector<HTMLDivElement>(".bm-tabs")!;
  const mainEl = root.querySelector<HTMLDivElement>(".bm-main")!;
  const handlers: Array<(t: BuildingType) => void> = [];

  let opened = false;
  let activeTab = 0;
  let lastPlayer: Cube | null = null;
  let tiles: TileRef[] = [];

  // Tab buttons are static — build once.
  const tabBtns = TABS.map((tab, i) => {
    const btn = document.createElement("div");
    btn.className = "bm-tab";
    btn.textContent = tab.label;
    btn.addEventListener("click", () => setTab(i));
    tabsEl.appendChild(btn);
    return btn;
  });

  function pick(type: BuildingType) {
    if (isLocked(type)) return;
    for (const h of handlers) h(type);
  }

  function renderTab() {
    const tab = TABS[activeTab]!;
    tabBtns.forEach((b, i) => b.classList.toggle("bm-tab--active", i === activeTab));
    mainEl.innerHTML = "";
    tiles = [];
    tab.types.forEach((type, idx) => {
      const lines = costLines(type);
      const locked = isLocked(type);
      const tile = document.createElement("div");
      tile.className = "bm-tile" + (locked ? " bm-tile--locked" : "");
      const costHtml = lines
        .map((l) => `<span class="bm-cost"><svg viewBox="0 0 13 13">${RES_ICON[l.key]}</svg>${l.amount}</span>`)
        .join("");
      tile.innerHTML = `
        <span class="bm-tile__hk">${idx + 1}</span>
        <div class="bm-tile__preview">${iconFor(type)}${locked ? `<div class="bm-tile__lock">🔒 ${REQUIREMENT[type]}</div>` : ""}</div>
        <div class="bm-tile__name">${nameFor(type)}</div>
        <div class="bm-cost-row">${costHtml}</div>
        <div class="bm-tip"><div class="bm-tip__title">${nameFor(type)}</div><div class="bm-tip__desc">${BLURB[type]}</div></div>`;
      tile.addEventListener("click", () => pick(type));
      mainEl.appendChild(tile);
      const costEls = Array.from(tile.querySelectorAll<HTMLSpanElement>(".bm-cost"));
      tiles.push({ type, el: tile, costEls, lines });
    });
    applyAffordability();
  }

  function applyAffordability() {
    for (const t of tiles) {
      if (isLocked(t.type)) continue;
      let any = false;
      t.lines.forEach((l, i) => {
        const short = lineShort(lastPlayer, l.key, l.amount);
        t.costEls[i]?.classList.toggle("bm-cost--short", short);
        if (short) any = true;
      });
      t.el.classList.toggle("bm-tile--unaffordable", any);
    }
  }

  function setTab(i: number) {
    activeTab = (i + TABS.length) % TABS.length;
    renderTab();
  }
  function cycleTab(dir: 1 | -1 = 1) { setTab(activeTab + dir); }
  function selectHotkey(n: number) {
    const type = TABS[activeTab]?.types[n - 1];
    if (type) pick(type);
  }

  // Sit the panel as a shelf just above the lower-HUD stack. Anchor to the
  // expertise arc (which sits just above the main plate); fall back to the plate
  // itself, then a CSS default. Re-read on each open so it tracks HUD changes.
  function positionAboveHud() {
    const ref = document.querySelector(".exp-arc") ?? document.getElementById("main-plate");
    if (!ref) return;
    const r = ref.getBoundingClientRect();
    if (r.height === 0) return; // not laid out yet — keep CSS fallback
    root.style.bottom = `${Math.round(window.innerHeight - r.top + 10)}px`;
  }

  function open() { opened = true; root.style.display = "flex"; renderTab(); positionAboveHud(); }
  function close() { opened = false; root.style.display = "none"; }
  function toggle() { opened ? close() : open(); }
  function isOpen() { return opened; }
  function refresh(player: Cube | null) {
    lastPlayer = player;
    if (opened) applyAffordability();
  }
  function onSelect(handler: (t: BuildingType) => void) { handlers.push(handler); }

  function getCost(type: BuildingType): string {
    const parts = costLines(type).map((l) => {
      const label = l.key === "ironOre" ? "Iron" : l.key === "woolOrLeather" ? "Wool/Leather" : l.key[0]!.toUpperCase() + l.key.slice(1);
      return `${l.amount} ${label}`;
    });
    return parts.join(", ") || "—";
  }

  return { toggle, open, close, isOpen, refresh, onSelect, cycleTab, selectHotkey, getCost };
}
